import { Loader2, Crown, ExternalLink, RotateCcw } from 'lucide-react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TIER_ORDER, getTierDisplayName } from '@papper/shared/reedle/tiers';
import { hideStripePaymentUI, platform } from '../lib/native';
import { getNativePrices } from '../lib/revenuecat';
import { ICON_SIZE } from '../design-system';

const UsagePlanTab = ({
  subscriptionLoading,
  subscription,
  currentTier,
  isOnTrial,
  effectiveTier,
  actionLoading,
  previewLoading,
  billingCycle,
  canChangeBillingCycle,
  handleBillingCycleClick,
  handlePlanAction,
  handleManageBilling,
  handleCancelDowngrade,
  planTiers,
  onSwitchToBalance,
  pendingTierActivation,
  handleRestorePurchases,
  restoreLoading,
}) => {
  const { t } = useTranslation('modals');
  // On native, use StoreKit-sourced prices (set by prefetchOfferings after auth).
  // On web, prices come from planTiers (USD config) as before.
  const nativePrices = platform.isNative ? getNativePrices() : {};

  return (
    <div className="usage-subscription-section">
      {subscriptionLoading ? (
        <div className="usage-loading">{t('common.loading', { defaultValue: 'Loading...' })}</div>
      ) : (
        <>
          {/* Current Plan */}
          <div className="usage-current-plan">
            <div className="usage-plan-header">
              <Crown size={ICON_SIZE.lg} className="usage-plan-icon" />
              <span className="usage-plan-label">{t('usage.plan.currentPlan')}</span>
            </div>
            <div className="usage-plan-info">
              <span className={`usage-plan-tier-badge ${currentTier}`}>
                {getTierDisplayName(currentTier)}
              </span>
              {isOnTrial && (
                <span className="usage-plan-trial-badge">
                  {t('usage.plan.trial')}
                </span>
              )}
              {currentTier !== 'free' && subscription?.billing_cycle && !isOnTrial && (
                <span className="usage-plan-billing-cycle">
                  ({subscription.billing_cycle === 'annual' ? t('usage.plan.annual') : t('usage.plan.monthly')})
                </span>
              )}
              {currentTier !== 'free' && subscription?.current_period_end && (
                <span className="usage-plan-expiry">
                  {subscription?.renewal_mode !== 'none' && new Date(subscription.current_period_end) < new Date() ? (
                    <span style={{ color: 'var(--color-info)' }}>{t('usage.plan.renewingWaitingForPayment')}</span>
                  ) : (
                    <>
                      {subscription?.renewal_mode !== 'none'
                        ? t('usage.plan.renewsOn', { date: new Date(subscription.current_period_end).toLocaleDateString() })
                        : t('usage.plan.expiresOn', { date: new Date(subscription.current_period_end).toLocaleDateString() })}
                      {subscription?.days_remaining > 0 && ` ${t('usage.plan.daysRemaining', { count: subscription.days_remaining })}`}
                    </>
                  )}
                </span>
              )}
              {subscription?.pending_downgrade_tier && (
                <div className="usage-plan-pending-downgrade-container">
                  <span className="usage-plan-pending-downgrade">
                    {t('usage.plan.pendingDowngrade', {
                      tier: getTierDisplayName(subscription.pending_downgrade_tier),
                      date: subscription.pending_downgrade_at
                        ? new Date(subscription.pending_downgrade_at).toLocaleDateString()
                        : new Date(subscription.current_period_end).toLocaleDateString()
                    })}
                  </span>
                  {!hideStripePaymentUI && (
                    <button
                      className="usage-plan-cancel-downgrade-btn"
                      onClick={handleCancelDowngrade}
                      disabled={actionLoading === 'cancel-downgrade'}
                      title={t('usage.plan.cancelDowngrade')}
                    >
                      {actionLoading === 'cancel-downgrade' ? (
                        <Loader2 size={ICON_SIZE.xxs} className="spin" />
                      ) : (
                        <X size={ICON_SIZE.xxs} />
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
            {currentTier !== 'free' && !isOnTrial && (
              <button
                className="usage-plan-manage-btn"
                onClick={handleManageBilling}
                disabled={actionLoading === 'portal'}
              >
                {actionLoading === 'portal' ? (
                  <><Loader2 size={ICON_SIZE.xs} className="spin" /> {t('usage.plan.processing')}</>
                ) : (
                  <>{t('usage.plan.manageBilling')} <ExternalLink size={ICON_SIZE.xs} /></>
                )}
              </button>
            )}
          </div>

          <>
            {/* Available Plans */}
            <div className="usage-plans-header">
              <span>{t('usage.plan.choosePlan')}</span>
            </div>

            {/* Billing Cycle Toggle */}
            <div className="usage-billing-toggle">
              <button
                className={`usage-billing-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
                onClick={() => handleBillingCycleClick('monthly')}
                disabled={!canChangeBillingCycle}
              >
                {t('usage.plan.monthly')}
              </button>
              <button
                className={`usage-billing-btn ${billingCycle === 'annual' ? 'active' : ''}`}
                onClick={() => handleBillingCycleClick('annual')}
                disabled={!canChangeBillingCycle}
              >
                {t('usage.plan.annual')}
                <span className="usage-billing-save">{t('usage.plan.save20')}</span>
              </button>
            </div>

            {/* Hint for locked billing cycle */}
            {!canChangeBillingCycle && (
              <div className="usage-billing-hint">
                {t('usage.plan.billingCycleLockedHint')}
              </div>
            )}

            <div className="usage-plans-grid">
              {TIER_ORDER.map((tierKey) => {
                const tier = planTiers[tierKey];
                const isCurrent = tierKey === effectiveTier;
                const currentIndex = TIER_ORDER.indexOf(effectiveTier);
                const tierIndex = TIER_ORDER.indexOf(tierKey);

                let actionKey;
                if (isCurrent) {
                  actionKey = 'current';
                } else if (tierKey === 'free') {
                  actionKey = 'downgrade';
                } else if (tierIndex > currentIndex) {
                  actionKey = effectiveTier === 'free' ? 'subscribe' : 'upgrade';
                } else {
                  actionKey = 'downgrade';
                }

                return (
                  <div
                    key={tierKey}
                    className={`usage-plan-card ${isCurrent ? 'current' : ''}`}
                  >
                    <div className="usage-plan-card-name">{tier.name}</div>
                    <div className="usage-plan-card-credits">
                      {t('usage.plan.creditsPerMonth', { count: tier.monthlyCredits.toLocaleString() })}
                    </div>
                    <div className="usage-plan-card-price">
                      {tier.priceMonthly === 0
                        ? t('usage.plan.free')
                        : platform.isNative
                          ? (nativePrices[`${tierKey}:${billingCycle}`] ?? '—')
                          : billingCycle === 'monthly'
                            ? t('usage.plan.priceMonthly', { symbol: '$', price: tier.priceMonthly })
                            : t('usage.plan.priceAnnual', { symbol: '$', price: tier.priceAnnual })}
                    </div>
                    <div className="usage-plan-card-features">
                      {tier.features.map((feature) => (
                        <div key={feature} className="usage-plan-card-feature">
                          {t(`usage.plan.features.${feature}`)}
                        </div>
                      ))}
                    </div>
                    <button
                      className={`usage-plan-card-btn ${isCurrent ? 'current' : ''} ${actionKey === 'downgrade' ? 'downgrade' : ''} ${actionKey === 'upgrade' || actionKey === 'subscribe' ? 'upgrade' : ''}`}
                      disabled={isCurrent || actionLoading?.startsWith('subscribe:') || previewLoading !== null || pendingTierActivation !== null}
                      onClick={() => !isCurrent && handlePlanAction(tierKey)}
                    >
                      {actionLoading === `subscribe:${tierKey}` || previewLoading === tierKey ? (
                        <Loader2 size={ICON_SIZE.xs} className="spin" />
                      ) : pendingTierActivation === tierKey ? (
                        <><Loader2 size={ICON_SIZE.xs} className="spin" /> {t('usage.plan.actions.activating', { defaultValue: 'Activating...' })}</>
                      ) : (
                        t(`usage.plan.actions.${actionKey}`)
                      )}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="usage-plan-footer">
              <span>{t('usage.plan.needHelp')} </span>
              <a href="https://t.me/dimpurr" target="_blank" rel="noopener noreferrer">
                {t('usage.plan.contact')}
              </a>
            </div>
            <div className="usage-plan-or-topup">
              <button
                className="usage-plan-or-topup-btn"
                onClick={onSwitchToBalance}
              >
                {t('usage.upsell.orTopUpCredits')}
              </button>
            </div>

            {/* Restore Purchases — required by App Store guidelines, native only */}
            {platform.isNative && (
              <button
                className="usage-restore-btn"
                onClick={handleRestorePurchases}
                disabled={restoreLoading || actionLoading !== null}
              >
                {restoreLoading ? (
                  <><Loader2 size={ICON_SIZE.xs} className="spin" /> {t('common.processing', { defaultValue: 'Processing...' })}</>
                ) : (
                  <><RotateCcw size={ICON_SIZE.xs} /> {t('usage.restorePurchases')}</>
                )}
              </button>
            )}
          </>
        </>
      )}
    </div>
  );
};

export default UsagePlanTab;
